import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { CampaignDashboardService } from '../../service/campaign-dashboard.service';
import { CampaignDashboardActions } from './campaign-dashboard.actions';
import { of } from 'rxjs';
import { MessageService } from 'primeng/api';


@Injectable()
export class CampaignDashboardEffects {

  private readonly messageService = inject(MessageService);

  loadCampaignDashboards$ = createEffect(
    (cd: CampaignDashboardService = inject(CampaignDashboardService)) => {
      return this.actions$.pipe(
        ofType(CampaignDashboardActions.loadCampaignDashboards),
        mergeMap(({ campaignId, fields, parameters, dashboardType }) =>
          cd.getCampaignDashboard(campaignId, parameters, dashboardType).pipe(
            map((res) => CampaignDashboardActions.loadCampaignDashboardsSuccess({
              campaignDashboard: res,
              fields,
              dashboardType
            })),
            catchError((error) => of(CampaignDashboardActions.loadCampaignDashboardsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllCampaignDashboards$ = createEffect(
    (cd: CampaignDashboardService = inject(CampaignDashboardService)) => {
      return this.actions$.pipe(
        ofType(CampaignDashboardActions.loadAllCampaignDashboards),
        mergeMap(({ campaignId, parameters, fields, dashboardType }) =>
          cd.getCampaignDashboard(campaignId, parameters, dashboardType).pipe(
            map((res) => CampaignDashboardActions.loadAllCampaignDashboardsSuccess({
              campaignDashboard: res,
              fields,
              dashboardType
            })),
            catchError((error) => of(CampaignDashboardActions.loadAllCampaignDashboardsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAttestationCampaignInProgress$ = createEffect(
    (cd: CampaignDashboardService = inject(CampaignDashboardService)) => {
      return this.actions$.pipe(
        ofType(CampaignDashboardActions.loadAttestationCampaignInProgress),
        mergeMap(() =>
          cd.getAttestationCampaignInProgress().pipe(
            map((campaign) => CampaignDashboardActions.loadAttestationCampaignInProgressSuccess({ campaign })),
            catchError((error) => of(CampaignDashboardActions.loadAttestationCampaignInProgressFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadEvaluationCampaignInProgress$ = createEffect(
    (cd: CampaignDashboardService = inject(CampaignDashboardService)) => {
      return this.actions$.pipe(
        ofType(CampaignDashboardActions.loadEvaluationCampaignInProgress),
        mergeMap(() =>
          cd.getEvaluationCampaignInProgress().pipe(
            map((campaign) => CampaignDashboardActions.loadEvaluationCampaignInProgressSuccess({ campaign })),
            catchError((error) => of(CampaignDashboardActions.loadEvaluationCampaignInProgressFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllAttestationCampaigns$ = createEffect(
    (cd: CampaignDashboardService = inject(CampaignDashboardService)) => {
      return this.actions$.pipe(
        ofType(CampaignDashboardActions.loadAllAttestationCampaigns),
        mergeMap(() =>
          cd.getAllAttestationCampaigns().pipe(
            map((campaigns) => CampaignDashboardActions.loadAllAttestationCampaignsSuccess({ campaigns })),
            catchError((error) => of(CampaignDashboardActions.loadAllAttestationCampaignsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllEvaluationCampaigns$ = createEffect(
    (cd: CampaignDashboardService = inject(CampaignDashboardService)) => {
      return this.actions$.pipe(
        ofType(CampaignDashboardActions.loadAllEvaluationCampaigns),
        mergeMap(() =>
          cd.getAllEvaluationCampaigns().pipe(
            map((campaigns) => CampaignDashboardActions.loadAllEvaluationCampaignsSuccess({ campaigns })),
            catchError((error) => of(CampaignDashboardActions.loadAllEvaluationCampaignsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  openAttestationCampaign$ = createEffect(
    (cd: CampaignDashboardService = inject(CampaignDashboardService)) => {
      return this.actions$.pipe(
        ofType(CampaignDashboardActions.openAttestationCampaign),
        mergeMap(({ year, period }) =>
          cd.openAttestationCampaign(year, period).pipe(
            map((campaign) => CampaignDashboardActions.openAttestationCampaignSuccess({ campaign })),
            catchError((error) => {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to open campaign' });
              return of(CampaignDashboardActions.openAttestationCampaignFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  openEvaluationCampaign$ = createEffect(
    (cd: CampaignDashboardService = inject(CampaignDashboardService)) => {
      return this.actions$.pipe(
        ofType(CampaignDashboardActions.openEvaluationCampaign),
        mergeMap(({ year, period }) =>
          cd.openEvaluationCampaign(year, period).pipe(
            map((campaign) => CampaignDashboardActions.openEvaluationCampaignSuccess({ campaign })),
            catchError((error) => {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to open campaign' });
              return of(CampaignDashboardActions.openEvaluationCampaignFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  // L'apertura cambia la lista delle campagne: la si ricarica dal BE cosi anno/periodo
  // selezionabili e status restano allineati anche se il BE normalizza qualcosa.
  reloadCampaignsAfterOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CampaignDashboardActions.openAttestationCampaignSuccess,
        CampaignDashboardActions.openEvaluationCampaignSuccess
      ),
      map((action) => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Campaign opened' });
        return action.type === CampaignDashboardActions.openAttestationCampaignSuccess.type
          ? CampaignDashboardActions.loadAllAttestationCampaigns()
          : CampaignDashboardActions.loadAllEvaluationCampaigns();
      })
    )
  );

  downloadDashboard$ = createEffect(
    (cd: CampaignDashboardService = inject(CampaignDashboardService)) => {
      return this.actions$.pipe(
        ofType(CampaignDashboardActions.downloadDashboard),
        mergeMap(({ campaignId, query, dashboardType, timeZone }) =>
          cd.downloadDashboard(campaignId, query, dashboardType, timeZone).pipe(
            map(() => CampaignDashboardActions.downloadDashboardSuccess()),
            catchError((error) => {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to export dashboard' });
              return of(CampaignDashboardActions.downloadDashboardFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  downloadManageScope$ = createEffect(
    (cd: CampaignDashboardService = inject(CampaignDashboardService)) => {
      return this.actions$.pipe(
        ofType(CampaignDashboardActions.downloadManageScope),
        mergeMap(({ campaignId, query, dashboardType, timeZone }) =>
          cd.downloadManageScope(campaignId, query, dashboardType, timeZone).pipe(
            map(() => CampaignDashboardActions.downloadManageScopeSuccess()),
            catchError((error) => {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to export manage scope' });
              return of(CampaignDashboardActions.downloadManageScopeFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  constructor(private actions$: Actions) { }
}
